import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { Icon } from '@serota/icons/web';
import { cx } from '../lib';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  flat?: boolean;
  tone?: 'default' | 'crit' | 'brand' | 'sunken';
  pad?: 'md' | 'lg' | 'none';
}

export function Card({ flat, tone = 'default', pad = 'md', className, style, ...rest }: CardProps) {
  return (
    <div
      className={cx(
        'sr-card',
        flat && 'sr-card--flat',
        tone === 'crit' && 'sr-card--crit',
        tone === 'brand' && 'sr-card--brand',
        tone === 'sunken' && 'sr-card--sunken',
        pad === 'lg' && 'sr-card--pad-lg',
        className,
      )}
      style={pad === 'none' ? { padding: 0, ...style } : style}
      {...rest}
    />
  );
}

export function CardHead({ title, actions, sub }: { title: ReactNode; actions?: ReactNode; sub?: ReactNode }) {
  return (
    <div className="sr-card__head">
      <div>
        <div className="sr-card__title">{title}</div>
        {sub ? <div className="caption sr-muted">{sub}</div> : null}
      </div>
      {actions ? <div className="sr-row">{actions}</div> : null}
    </div>
  );
}

export function Divider({ style }: { style?: CSSProperties }) {
  return <hr className="sr-divider" style={style} />;
}

export interface AvatarProps {
  initials: string;
  size?: 'sm' | 'md' | 'lg';
  verified?: boolean;
  tone?: 'neutral' | 'brand' | 'route';
  src?: string | null;
  style?: CSSProperties;
}

/** Doğrulanmış kişi `sr-avatar__verified` ile (okul güvenliği). */
export function Avatar({ initials, size = 'md', verified, tone = 'neutral', src, style }: AvatarProps) {
  const toneStyle: CSSProperties =
    tone === 'brand'
      ? { background: 'var(--brand-tint)', color: 'var(--brand-ink)' }
      : tone === 'route'
        ? { background: 'var(--route-tint)', color: 'var(--route-ink)' }
        : {};
  return (
    <span
      className={cx('sr-avatar', size === 'sm' && 'sr-avatar--sm', size === 'lg' && 'sr-avatar--lg')}
      style={{ ...toneStyle, ...style }}
    >
      {src ? (
        <img src={src} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
      ) : (
        initials
      )}
      {verified ? (
        <span className="sr-avatar__verified" aria-label="Doğrulandı">
          <Icon name="check" />
        </span>
      ) : null}
    </span>
  );
}

export function KV({ k, v, mono, style }: { k: ReactNode; v: ReactNode; mono?: boolean; style?: CSSProperties }) {
  return (
    <div className="sr-kv" style={style}>
      <span className="sr-kv__k">{k}</span>
      <span className={cx('sr-kv__v', mono && 'sr-plate-text')}>{v}</span>
    </div>
  );
}

/** Plaka metni: her zaman mono, boşluklu. */
export function PlateText({ plate, size }: { plate: string; size?: number }) {
  return (
    <span className="sr-plate-text" style={size ? { fontSize: size } : undefined}>
      {plate}
    </span>
  );
}

/** TR şeritli plaka rozeti. */
export function Plate({ plate, compact }: { plate: string; compact?: boolean }) {
  return (
    <span className="sr-plate" style={compact ? { height: 34 } : undefined} aria-label={`Plaka ${plate}`}>
      <span className="sr-plate__tr" style={compact ? { fontSize: 9 } : undefined}>
        TR
      </span>
      <span className="sr-plate__no" style={compact ? { fontSize: 13, letterSpacing: 1, padding: '0 8px' } : undefined}>
        {plate}
      </span>
    </span>
  );
}
